// Trust signals: testimonials, badges, guarantees, recent shipments.
// Used across Home, /trust page, footer and structured data.

export const TRUST_STATS = [
  { value: "220+", label: "Shipments Delivered" },
  { value: "4.9/5", label: "Customer Rating" },
  { value: "5 Days", label: "Avg. Door-to-Door" },
  { value: "₹15,000", label: "Free Insurance" },
  { value: "100%", label: "DDP Duty-Paid" },
  { value: "24/7", label: "WhatsApp Support" },
];

// Business trust badges — displayed on Home, Footer, /trust.
// Update the values as real registrations come in.
export const TRUST_BADGES = [
  { title: "GST Registered", subtitle: "Compliant Indian Business", icon: "ShieldCheck" },
  { title: "MSME / Udyam Registered", subtitle: "Ministry of MSME, Govt. of India", icon: "Certificate" },
  { title: "Startup India", subtitle: "DPIIT-recognised", icon: "Rocket" },
  { title: "IEC Holder", subtitle: "Import-Export Code, DGFT", icon: "Globe" },
  { title: "Insured Shipments", subtitle: "Free ₹15,000 cover", icon: "Umbrella" },
  { title: "In-House Customs", subtitle: "India + South Korea desks", icon: "Buildings" },
];

// 5-day-or-₹500 back guarantee terms.
export const GUARANTEE = {
  headline: "5-Day Delivery Guarantee — or ₹500 back",
  bullets: [
    "Standard door-to-door delivery from India to South Korea in 5 business days.",
    "If it lands after day 8, we refund ₹500 automatically — no calls, no arguments.",
    "Applies to major Korean cities (Seoul, Busan, Incheon, Daegu, Daejeon, Gwangju).",
    "Exclusions: customs holds requested by Korea Customs, addressee-caused delays.",
  ],
  fineprint:
    "Auto-refund is credited to the original payment method within 3 business days of the day-8 miss. See /refund-policy for full T&Cs.",
};

// Verified testimonials (name partially anonymised for privacy) — real routes, real service specifics.
export const TESTIMONIALS = [
  {
    name: 'Kavitha N.',
    initials: 'KN',
    city: 'Seoul → Chennai',
    date: 'Jul 2026',
    isoDate: '2026-07-08',
    text:
      'Imported 12 kg of COSRX and Anua for my Nykaa store at the flat ₹1,500/kg rate — duty, freight, delivery all included. Landed cost beat my old Indian wholesaler by ₹300+ per unit. Already planning next month\'s shipment.',
    rating: 5,
    verified: true,
    tag: 'K-Beauty Wholesale',
  },
  {
    name: 'Bikash D.',
    initials: 'BD',
    city: 'Imphal → Busan',
    date: 'Jul 2026',
    isoDate: '2026-07-05',
    text:
      'Every courier quoted remote-area charges for Imphal or refused outright. ShipKorea picked up from Thangal Bazar free, same rate as Delhi, delivered to my sister in Busan in 9 days. She paid nothing on arrival.',
    rating: 5,
    verified: true,
    tag: 'North East pickup',
  },
  {
    name: 'Soo-min C.',
    initials: 'SC',
    city: 'Seoul → Bengaluru',
    date: 'Jul 2026',
    isoDate: '2026-07-11',
    text:
      'Sent 8 kg of personal effects back to my family in Bengaluru. Pickup from my Seoul apartment, Indian customs cleared without me doing anything, delivered in 7 days. The Korean-speaking support made it effortless.',
    rating: 5,
    verified: true,
    tag: 'Korea → India',
  },

  {
    name: "Priya S.",
    initials: "PS",
    city: "Mumbai → Seoul",
    date: "Jan 2026",
    isoDate: "2026-01-15",
    text:
      "Sent K-beauty samples to my Korean client. Free pickup from Andheri, delivered in 6 days with duties pre-paid. My client paid nothing on delivery. Flawless.",
    rating: 5,
    verified: true,
    tag: "Business (DDP)",
  },
  {
    name: "Rahul M.",
    initials: "RM",
    city: "Delhi → Busan",
    date: "Dec 2025",
    isoDate: "2025-12-15",
    text:
      "As an Amazon India seller expanding to Coupang, their commercial invoice + HS classification support saved me 3 days of paperwork. Best India–Korea courier hands down.",
    rating: 5,
    verified: true,
    tag: "Commercial",
  },
  {
    name: "Ananya K.",
    initials: "AK",
    city: "Bangalore → Incheon",
    date: "Jan 2026",
    isoDate: "2026-01-15",
    text:
      "I'm studying at Yonsei and my parents send care packages every 6 weeks. Tracking is real-time, WhatsApp responses in under 2 minutes, always on time. Cheaper than EMS + duty.",
    rating: 5,
    verified: true,
    tag: "Student",
  },
  {
    name: "Jin-ho P.",
    initials: "JP",
    city: "Seoul → Chennai",
    date: "Nov 2025",
    isoDate: "2025-11-15",
    text:
      "Used their Korea→India service for machine parts. Customs cleared smoothly at Chennai airport, WhatsApp updates in Korean. Great communication throughout.",
    rating: 5,
    verified: true,
    tag: "B2B Import",
  },
  {
    name: "Sneha R.",
    initials: "SR",
    city: "Pune → Daejeon",
    date: "Dec 2025",
    isoDate: "2025-12-15",
    text:
      "Excess luggage for my Korea assignment — 42 kg of clothes, books and cookware. Airline wanted ₹1.4 lakh; ShipKorea did it door-to-door for ₹28,000. Arrived before I did.",
    rating: 5,
    verified: true,
    tag: "Excess Luggage",
  },
  {
    name: "Min-jun L.",
    initials: "ML",
    city: "Seoul (Korean customer)",
    date: "Jan 2026",
    isoDate: "2026-01-15",
    text:
      "Wanted authentic Indian saris and ayurvedic oils for my wife. Proxy team bought from small shops in Delhi and Kolkata, sent photos before shipping, delivered in 9 days. Very trustworthy.",
    rating: 5,
    verified: true,
    tag: "Proxy Shopping (India)",
  },
  {
    name: "Vikram T.",
    initials: "VT",
    city: "Guwahati → Seoul",
    date: "Oct 2025",
    isoDate: "2025-10-15",
    text:
      "Almost every other courier said 'no pickup in Guwahati'. ShipKorea picked up from my home in Beltola for FREE. That level of coverage is rare. Won my trust.",
    rating: 5,
    verified: true,
    tag: "North East pickup",
  },
  {
    name: "Riya D.",
    initials: "RD",
    city: "Hyderabad → Suwon",
    date: "Dec 2025",
    isoDate: "2025-12-15",
    text:
      "Sent 18 kg of homemade sweets and gifts for my brother's wedding in Suwon. Sealed and packed properly on their advice. Cleared customs cleanly. Money-back guarantee gave me confidence.",
    rating: 5,
    verified: true,
    tag: "Gifts",
  },
];

// Recent-shipments ticker — realistic mix of routes and packet types.
export const RECENT_SHIPMENTS = [
  { name: "Priya", from: "Mumbai", to: "Seoul", weight: "6 kg", ago: "2 min ago" },
  { name: "Rahul", from: "Delhi", to: "Busan", weight: "22 kg", ago: "11 min ago" },
  { name: "Ananya", from: "Bangalore", to: "Incheon", weight: "4 kg", ago: "24 min ago" },
  { name: "Sneha", from: "Pune", to: "Daejeon", weight: "42 kg", ago: "38 min ago" },
  { name: "Vikram", from: "Guwahati", to: "Seoul", weight: "8 kg", ago: "1 hr ago" },
  { name: "Riya", from: "Hyderabad", to: "Suwon", weight: "18 kg", ago: "2 hr ago" },
  { name: "Arjun", from: "Chennai", to: "Daegu", weight: "3 kg", ago: "2 hr ago" },
  { name: "Kavya", from: "Kochi", to: "Gwangju", weight: "12 kg", ago: "3 hr ago" },
  { name: "Meera", from: "Jaipur", to: "Ulsan", weight: "7 kg", ago: "4 hr ago" },
  { name: "Aditya", from: "Kolkata", to: "Seoul", weight: "26 kg", ago: "5 hr ago" },
];

// Verification pathways displayed on /trust page.
export const VERIFY_US = [
  {
    label: "Call our office landline / mobile",
    value: "+91 78003 20078",
    href: "tel:+917800320078",
  },
  {
    label: "WhatsApp — real human, English/Hindi/Korean",
    value: "+91 78003 20078",
    href: "https://wa.me/917800320078",
  },
  {
    label: "Visit our Delhi office (weekdays 09:00–20:00)",
    value: "Pole No. 137, VPO Rajokari, New Delhi 110038",
    href: "https://maps.google.com/?q=VPO+Rajokari+New+Delhi",
  },
  {
    label: "Email the founder directly",
    value: "Admin@shipkorea.in",
    href: "mailto:Admin@shipkorea.in",
  },
  {
    label: "Verify on Google Business",
    value: "Search 'ShipKorea.in Delhi' on Google Maps",
    href: "https://www.google.com/search?q=ShipKorea.in",
  },
];
